import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, Plus, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentMethods = () => {
  const { profile } = useOutletContext();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/stripe/payment-methods`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentMethods(response.data.payment_methods || []);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    } finally {
      setLoading(false);
    }
  };

  const openStripePortal = async () => {
    setPortalLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/stripe/portal-session`, 
        { return_url: window.location.href },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Open Stripe Customer Portal in new tab
      window.open(response.data.url, '_blank');
      toast.success('Opening payment portal...');
    } catch (error) {
      console.error('Error opening portal:', error);
      toast.error('Failed to open payment portal');
    } finally {
      setPortalLoading(false);
    }
  };

  const getCardBrandIcon = (brand) => {
    const brandColors = {
      visa: 'text-blue-600',
      mastercard: 'text-red-500',
      amex: 'text-blue-400',
      discover: 'text-orange-500'
    };
    return brandColors[brand?.toLowerCase()] || 'text-gray-600';
  };

  return (
    <div className="space-y-6" data-testid="payment-methods">
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-heading font-bold text-maroon">Payment Methods</h2>
            <p className="text-maroon/70 mt-1">Manage your saved payment methods securely via Stripe</p>
          </div>
          <Button 
            onClick={openStripePortal}
            disabled={portalLoading}
            className="btn-primary"
            data-testid="manage-payments-btn"
          >
            {portalLoading ? (
              'Opening...'
            ) : (
              <>
                <ExternalLink size={18} className="mr-2" />
                Manage Payments
              </>
            )}
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-maroon"></div>
          </div>
        ) : paymentMethods.length === 0 ? (
          <div className="text-center py-12 bg-sand/20 rounded-sm">
            <CreditCard size={48} className="mx-auto text-maroon/30 mb-4" />
            <p className="text-maroon/70 mb-4">No payment methods saved yet</p>
            <Button 
              onClick={openStripePortal}
              disabled={portalLoading}
              variant="outline"
              className="border-maroon text-maroon hover:bg-maroon hover:text-white"
            >
              <Plus size={18} className="mr-2" />
              Add Payment Method
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {paymentMethods.map((method) => (
              <div 
                key={method.id}
                className="flex items-center justify-between p-4 bg-sand/20 rounded-sm border border-maroon/10"
              >
                <div className="flex items-center gap-4">
                  <div className={`p-2 bg-white rounded-sm ${getCardBrandIcon(method.brand)}`}>
                    <CreditCard size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-maroon capitalize">
                      {method.brand} •••• {method.last4}
                    </p>
                    <p className="text-sm text-maroon/60">
                      Expires {method.exp_month}/{method.exp_year}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            
            <p className="text-xs text-maroon/50 mt-4">
              To add, remove, or update payment methods, click "Manage Payments" to access the secure Stripe portal.
            </p>
          </div>
        )}
      </div>

      {/* Security Note */}
      <div className="card bg-sand/30">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-green-100 rounded-full">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <div>
            <h3 className="font-semibold text-maroon">Your payment information is secure</h3>
            <p className="text-sm text-maroon/70 mt-1">
              We use Stripe to securely store and process your payment information. 
              Your card details are never stored on our servers and are protected by 
              industry-leading encryption.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethods;
